import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from "react";
import { PartnershipsStoreContext } from "../../Store/PartnershipsStore";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { Box, Typography, Button, Chip } from "@mui/material";
import { getPartnerships, updatePartnershipSortingPosition } from "../../Requests/PartnershipService";
import { useNavigate } from "react-router-dom";
import { Add } from "@mui/icons-material";
import AddModalView from "../AddModalView/AddModalView";
import AddPartnership from "./AddPartnership";
import { VISIBLE_STATE_CASES } from "../../Models/StateConstants.ts";
import { useRestoreScrollPosition } from "../../Hooks/useScrollRestoration";
import { useTableState } from "../../Hooks/useTableState";
import { useFilteredBySearch } from "../../Hooks/useFilteredBySearch";
import TableToolbarWithSearch from "../TableToolbar/TableToolbarWithSearch.jsx";
import ColumnsIcon from "../TableToolbar/ColumnsIcon.jsx";


const COLUMNS = [
  {
    accessorKey: 'title',
    header: 'Name',
    Cell: ({ renderedCellValue, row }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <img
          alt="avatar"
          height={32}
          width={32}
          src={row.original.logoImage}
          loading="lazy"
          style={{ borderRadius: '50%' }}
        />
        <span>{renderedCellValue}</span>
      </Box>
    )
  },
  {
    accessorKey: 'shortDescription',
    header: 'Organisation',
    enableGlobalFilter: false,
  },
  {
    accessorKey: 'hiddenState',
    header: 'Visibility',
    enableGlobalFilter: false,
    filterVariant: 'select',
    filterSelectOptions: VISIBLE_STATE_CASES.map((value) => value.label),
    Cell: ({ cell }) => (
      <Chip label={cell.getValue()} />
    )
  }
]

const SEARCH_FIELD_OPTIONS = [
  { value: 'title', label: 'Name' },
  { value: 'shortDescription', label: 'Organisation' }
]

export default function Partnerships() {

  const store = useContext(PartnershipsStoreContext)

  const [columnVisibility, setColumnVisibility] = useState(store.columnVisibility)
  useEffect(() => {
    store.columnVisibility = columnVisibility
  }, [columnVisibility, store])

  const [columnFilters, setColumnFilters] = useState(store.columnFilters)
  useEffect(() => {
    store.columnFilters = columnFilters
  }, [columnFilters, store])
  const [globalFilter, setGlobalFilter] = useTableState(store, 'globalFilter', undefined)
  const [searchField, setSearchField] = useTableState(store, 'searchField', SEARCH_FIELD_OPTIONS[0].value)

  const [pagination, setPagination] = useState(store.pagination)
  useEffect(() => {
    store.pagination = pagination
  }, [pagination, store])

  const navigate = useNavigate()
  const tableContainerRef = useRef(null)

  // Use store for state preservation
  const [open, setOpen] = useState(store.open)
  useEffect(() => {
    store.open = open
  }, [open, store])

  const [isLoading, setIsloading] = useState(store.isLoading)
  useEffect(() => {
    store.isLoading = isLoading
  }, [isLoading, store])

  const [isRefetching, setIsRefetching] = useState(store.isRefetching)
  useEffect(() => {
    store.isRefetching = isRefetching
  }, [isRefetching, store])

  const [tableData, setTableData] = useState(store.tableData)
  useEffect(() => {
    store.tableData = tableData
  }, [tableData, store])

  const sortByPosition = useCallback((list) =>
    [...list].sort((a, b) => (a.sortingPosition ?? 99) - (b.sortingPosition ?? 99)), [])

  const fetchData = useCallback(async () => {
    const result = await getPartnerships()
    setIsloading(false)
    setIsRefetching(false)
    setTableData(sortByPosition(result))
  }, [sortByPosition])

  useEffect(() => {
    // Restore state from store if data exists
    if (store.tableData.length > 0 && !store.needsRefresh) {
      setTableData(sortByPosition(store.tableData))
      setIsloading(false)
      setIsRefetching(false)
    } else {
      // Fetch data if not loaded or refresh is needed
      if (store.needsRefresh) {
        store.needsRefresh = false
      }
      fetchData()
    }
  }, [store, fetchData, sortByPosition])

  // Restore scroll position after table is rendered and data is loaded
  useRestoreScrollPosition(tableContainerRef, store, tableData, isLoading)

  const columns = useMemo(() => COLUMNS, [])
  const data = useFilteredBySearch(tableData, globalFilter, searchField)

  const handleDragEnd = useCallback(({ table }) => {
    const { draggingRow, hoveredRow } = table.getState()
    if (!hoveredRow || !draggingRow || draggingRow.id === hoveredRow.id) return
    const tableDataIds = tableData.map((r) => r.objectId)
    const fromIdx = tableDataIds.indexOf(draggingRow.original.objectId)
    const toIdx = tableDataIds.indexOf(hoveredRow.original.objectId)
    if (fromIdx === -1 || toIdx === -1) return
    const next = [...tableData]
    const [item] = next.splice(fromIdx, 1)
    next.splice(toIdx, 0, item)
    const withPositions = next.map((row, i) => ({ ...row, sortingPosition: i + 1 }))
    setTableData(withPositions)
    const oldPosById = Object.fromEntries(tableData.map((r) => [r.objectId, r.sortingPosition]))
    const updates = withPositions.filter((row) => oldPosById[row.objectId] !== row.sortingPosition)
    if (updates.length === 0) return
    Promise.all(
      updates.map((row) => updatePartnershipSortingPosition(row.objectId, row.sortingPosition))
    ).catch((err) => console.error("Failed to save partnership order", err))
  }, [tableData])

  const table = useMaterialReactTable({
    columns,
    data,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableGlobalFilter: false,
    enableRowOrdering: true,
    enableSorting: false,
    muiRowDragHandleProps: ({ table }) => ({
      onDragEnd: () => handleDragEnd({ table }),
    }),
    state: {
      isLoading: isLoading,
      showProgressBars: isRefetching,
      columnFilters: columnFilters,
      columnVisibility: columnVisibility,
      globalFilter: globalFilter,
      showGlobalFilter: false,
      pagination: pagination
    },
    icons: {
      ViewColumnIcon: ColumnsIcon,
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        // Save scroll position before navigation
        if (tableContainerRef.current) {
          store.scrollTop = tableContainerRef.current.scrollTop
        }
        navigate(`/partnership/${row.original.objectId}`)
      },
      sx: {
        cursor: 'pointer'
      }
    }),
    renderTopToolbar: ({ table }) => (
      <TableToolbarWithSearch
        table={table}
        exportFileName="partnerships"
        searchFieldOptions={SEARCH_FIELD_OPTIONS}
        searchField={searchField}
        onSearchFieldChange={setSearchField}
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        selectMinWidth={140}
        action={
          <Button
            variant="contained"
            size="large"
            onClick={() => setOpen(true)}
          >
            <Add />
            Add Partnerships
          </Button>
        }
      />
    ),
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid",
        color: "grey.300"
      }
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: {
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto'
      },
      onScroll: (e) => {
        // Save scroll position while scrolling
        if (e.target instanceof HTMLElement) {
          store.scrollTop = e.target.scrollTop
        }
      }
    }
  })

  return (
    <>
      <Box
        paddingTop={2}
        paddingBottom={1}
        display="flex"
        justifyContent="space-between"
      >
        <Typography
          variant="h4"
        >
          Partnerships
        </Typography>
      </Box>

      <MaterialReactTable
        table={table}
      />

      <AddModalView
        isOpen={open}
        setIsOpen={setOpen}
        title={"Add Partnership"}
        view={
          <AddPartnership publish={() => {
            setOpen(false)
            setIsRefetching(true)
            fetchData()
          }} />}
      />
    </>
  )
}
